import {
    defineConnectorConstellationDerivation,
    defineConnectorItemDerivation,
    defineEntityConstellationDerivation,
    defineEntityItemDerivation,
    toConnectorItemCollectionDerivation,
    toEntityItemCollectionDerivation,
} from '@toolproof-core/visualization/derivation/_pure/derivation-values';
import { buildExtrudedProfileShape, buildLineGuideShape, buildSphereShape } from '@toolproof-core/visualization/_pure/shapes';
import type {
    ConnectorConstellationDerivation,
    ConnectorItemCollectionDerivation,
    EntityConstellationDerivation,
    EntityItemCollectionDerivation,
} from '@toolproof-core/visualization/derivation/_contract/derivation';

import {
    toBooleanProfileCollectionKey,
    toBooleanProfileConstellationKey,
    toBooleanProfileItemKey,
    toBooleanRodAnchorCollectionKey,
    toBooleanRodAnchorConstellationKey,
    toBooleanRodAnchorItemKey,
    toBooleanRodCollectionKey,
    toBooleanRodConstellationKey,
    toBooleanRodItemKey,
} from '@/adapters/primitive/_pure/derivation/keyCodec';
import type { BooleanRodState } from '@/adapters/primitive/_contract/boolean/rod';

export function buildBooleanRodDerivations(rod: BooleanRodState): {
    entityItemCollectionDerivations: EntityItemCollectionDerivation[];
    entityConstellationDerivations: EntityConstellationDerivation[];
    connectorItemCollectionDerivation: ConnectorItemCollectionDerivation;
    connectorConstellationDerivation: ConnectorConstellationDerivation;
} {
    const entityItemCollectionDerivations: EntityItemCollectionDerivation[] = [];
    const entityConstellationDerivations: EntityConstellationDerivation[] = [];
    for (const segment of rod.segments) {
        if (segment.profile) {
            const { profile } = segment;
            const center: [number, number, number] = [0, 1, 2].map((index) => (
                (segment.from[index]! + segment.to[index]!) / 2
            )) as [number, number, number];
            const relative = (point: readonly [number, number, number]): [number, number, number] => [
                point[0] - center[0], point[1] - center[1], point[2] - center[2],
            ];
            const itemKey = toBooleanProfileItemKey(segment.segmentKey);
            entityItemCollectionDerivations.push(toEntityItemCollectionDerivation({
                itemCollectionKey: toBooleanProfileCollectionKey(segment.segmentKey),
                itemDerivations: [defineEntityItemDerivation({
                    item: { itemKind: 'entity', itemKey, name: segment.segmentKey },
                    parametricState: {
                        shape: buildExtrudedProfileShape({
                            ...profile,
                            from: relative(segment.from), to: relative(segment.to),
                            lateral: [...profile.lateral],
                        }),
                        visualStyle: { color: segment.color, opacity: segment.opacity },
                    },
                })],
            }));
            entityConstellationDerivations.push(defineEntityConstellationDerivation({
                constellation: {
                    constellationKind: 'entity',
                    constellationKey: toBooleanProfileConstellationKey(segment.segmentKey),
                    itemCollectionKey: toBooleanProfileCollectionKey(segment.segmentKey),
                    anchor: { itemKey, anchorKey: 'center' },
                },
                parametricState: {
                    layout: {
                        layoutKind: 'ring', anchor: { anchorKind: 'position', position: center }, radius: 0,
                    },
                },
            }));
            continue;
        }
        for (const endpoint of ['from', 'to'] as const) {
            const itemKey = toBooleanRodAnchorItemKey(segment.segmentKey, endpoint);
            entityItemCollectionDerivations.push(toEntityItemCollectionDerivation({
                itemCollectionKey: toBooleanRodAnchorCollectionKey(segment.segmentKey, endpoint),
                itemDerivations: [defineEntityItemDerivation({
                    item: { itemKind: 'entity', itemKey, name: `${segment.segmentKey} ${endpoint}` },
                    parametricState: { shape: buildSphereShape({ radius: 0.01 }), visualStyle: { opacity: 0 } },
                })],
            }));
            entityConstellationDerivations.push(defineEntityConstellationDerivation({
                constellation: {
                    constellationKind: 'entity',
                    constellationKey: toBooleanRodAnchorConstellationKey(segment.segmentKey, endpoint),
                    itemCollectionKey: toBooleanRodAnchorCollectionKey(segment.segmentKey, endpoint),
                    anchor: { itemKey, anchorKey: 'center' },
                },
                parametricState: {
                    layout: {
                        layoutKind: 'sector-ring',
                        anchor: { anchorKind: 'position', position: [...segment[endpoint]] as [number, number, number] },
                        innerRadius: 0,
                        outerRadius: 0.01,
                        angularGapFraction: 0,
                        arcSegments: 8,
                        rotation: [0, 0, 0],
                    },
                },
            }));
        }
    }

    return {
        entityItemCollectionDerivations,
        entityConstellationDerivations,
        connectorItemCollectionDerivation: toConnectorItemCollectionDerivation({
            itemCollectionKey: toBooleanRodCollectionKey(),
            itemDerivations: rod.segments.filter((segment) => !segment.profile).map((segment) => defineConnectorItemDerivation({
                item: {
                    itemKind: 'connector',
                    itemKey: toBooleanRodItemKey(segment.segmentKey),
                    shape: buildLineGuideShape(),
                    endpoints: {
                        from: { endpointKind: 'static', itemKey: toBooleanRodAnchorItemKey(segment.segmentKey, 'from'), anchorKey: 'center' },
                        to: { endpointKind: 'static', itemKey: toBooleanRodAnchorItemKey(segment.segmentKey, 'to'), anchorKey: 'center' },
                    },
                },
                parametricState: {
                    visualStyle: { color: segment.color, opacity: segment.opacity },
                    depthTest: false,
                },
            })),
        }),
        connectorConstellationDerivation: defineConnectorConstellationDerivation({
            constellation: {
                constellationKind: 'connector',
                constellationKey: toBooleanRodConstellationKey(),
                itemCollectionKey: toBooleanRodCollectionKey(),
            },
            parametricState: { visibility: { visible: true } },
        }),
    };
}